import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserManagementApiService } from '@core/services/api.services';
import { UserDataManagementModel, UserGroupDetailsModel, UserManagementModel } from '@shared/models';
import { filter, map } from 'rxjs/operators';
import {
  PaginatedResponseDto,
  UserDataDto,
  UserGroupDetailsDto,
  UserManagementUsersDto,
} from '@core/services/dto';
import { CustomSnackbarService } from '@core/services/custom-snackbar.service';
import { AuthService } from '@core/services/auth';
import { UserOfferUpdateApiRequest } from '@shared/models/requests/user-offer-update.request.dto';
import { getOfferApiBase } from '@config/org.config';
import { AllBranchManagementBranchesDto, BranchManagementBranchesDto } from '@core/services/dto/branch-management-branches.dto';
import { BranchManagementUrls } from '@core/constants/endpoints.constants';
import { AllBranchManagementModel, BranchManagementModel } from '@shared/models/branch-management.model';
import { UserBranchModel } from '@shared/models/user-Branch.model';
import { saveAsBlob } from '@shared/utils/common';
import { UserManagementChangeLogUserDto } from '@core/services/dto/userchangelog.dto';
import { UserManagementChangeLogModel } from '@shared/models/user-management-change-log.model';

const offerBase = getOfferApiBase();

@Injectable({ providedIn: 'any' })
export class UserManagementStateService {
  private users: BehaviorSubject<PaginatedResponseDto<UserManagementModel>> =
    new BehaviorSubject(null);

    private allUsers: BehaviorSubject<UserDataManagementModel[]> =
  new BehaviorSubject<UserDataManagementModel[]>([]);

    private usersChangeLog: BehaviorSubject<PaginatedResponseDto<UserManagementChangeLogModel>> =
    new BehaviorSubject(null);

  public users$: Observable<PaginatedResponseDto<UserManagementModel>> =
    this.users.asObservable().pipe(filter((x) => !!x));

    public usersChangeLog$: Observable<PaginatedResponseDto<UserManagementChangeLogModel>> =
    this.usersChangeLog.asObservable().pipe(filter((x) => !!x));

  private usersOffer: BehaviorSubject<
    PaginatedResponseDto<UserManagementModel>
  > = new BehaviorSubject(null);

  public usersOffer$: Observable<PaginatedResponseDto<UserManagementModel>> =
    this.usersOffer.asObservable().pipe(filter((x) => !!x));

  private user: BehaviorSubject<UserManagementModel> = new BehaviorSubject(
    null
  );

   public allusers$: Observable<UserDataManagementModel[]> =
    this.allUsers.asObservable().pipe(filter((x) => !!x));

  public user$: Observable<UserManagementModel> = this.user
    .asObservable()
    .pipe(filter((x) => !!x));

  private permission: BehaviorSubject<number[]> = new BehaviorSubject([]);

  public permission$: Observable<number[]> = this.permission
    .asObservable()
    .pipe(filter((x) => !!x));

  constructor(
    private service: UserManagementApiService,
    private authService: AuthService,
    private toast: CustomSnackbarService
  ) {}

  getData(request?): void {
    this.service
      .getData(request)
      .pipe(
        map((response: PaginatedResponseDto<UserManagementUsersDto>) => {
          return {
            ...response,
            data: response.data.map((item) => new UserManagementModel(item)),
          };
        })
      )
      .subscribe((data) => {
        this.users.next(data);
      });
  }

   getAllUserData(): void {
    this.service.getAllUserData()
      .pipe(
        map((users: UserDataDto[]) => users.map(user => new UserDataManagementModel(user)))
      )
      .subscribe((transformedUsers) => {
        this.allUsers.next(transformedUsers);
        return transformedUsers;
      });
  }

  getChangeLogData(request?): void {
    this.service
      .getUserChangeLogData(request)
      .pipe(
        map((response: PaginatedResponseDto<UserManagementChangeLogUserDto>) => {
          return {
            ...response,
            data: response.data.map((item) => new UserManagementChangeLogModel(item)),
          };
        })
      )
      .subscribe((data) => {
        this.usersChangeLog.next(data);
      });
  }

  getOfferData(request?): void {
    if (offerBase !== undefined) {
      this.service
        .getOfferData(request)
        .pipe(
          map((response: PaginatedResponseDto<UserManagementUsersDto>) => {
            return {
              ...response,
              data: response.data.map((item) => new UserManagementModel(item)),
            };
          })
        )
        .subscribe((data) => {
          this.usersOffer.next(data);
        });
    } else {
      this.usersOffer.next(null);
    }
  }

  getPermissionsReportFile(): void {
    this.service.getPermissionsReportFile()
      .subscribe((data: Blob) => saveAsBlob(data, 'PermissionReport.xlsx'));
  }

  savePermission(id, permissions): Observable<UserManagementUsersDto> {
    return this.service.saveUserPermission(id, permissions);
  }

  updateUser(id, request): Observable<UserManagementUsersDto> {
    return this.service.updateUserId(id, request);
  }

  getUserById(id): void {
    this.service
      .getUserId(id)
      .pipe(
        map(
          (response: UserManagementUsersDto) =>
            new UserManagementModel(response)
        )
      )
      .subscribe((data: UserManagementModel) => {
        this.user.next(data);
      });
  }

  getPermissions(id): void {
    this.service.getPermissions(id).subscribe((response) => {
      this.permission.next(response.data);
    });
  }

  getUserGroups(id): Observable<PaginatedResponseDto<UserGroupDetailsModel>> {
    return this.service.getUserGroups(id).pipe(
      map((response: PaginatedResponseDto<UserGroupDetailsDto>) => {
        return {
          ...response,
          data: response.data.map((item) => new UserGroupDetailsModel(item)),
        };
      })
    );
  }

  getGroups(request?): Observable<PaginatedResponseDto<UserGroupDetailsModel>> {
    return this.service.getGroups(request).pipe(
      map((response: PaginatedResponseDto<UserGroupDetailsDto>) => {
        return {
          ...response,
          data: response.data.map((item) => new UserGroupDetailsModel(item)),
        };
      })
    );
  }

  getBranches(request?): Observable<PaginatedResponseDto<AllBranchManagementModel>> {
  return this.service.getBranches(request).pipe(
    map((response: PaginatedResponseDto<AllBranchManagementBranchesDto>) => {
      console.log('API Response:', response);
      return {
        ...response,
        data: response.data.map((item) => new AllBranchManagementModel(item)),
      };
    })
  );
}

  saveGroups(id, groupIds): void {
    this.service.saveUserGroups(id, groupIds).subscribe(() => {
      this.toast.success('User group was successfully updated');
      // window.location.reload();
    });
  }

  clear(): void {
    this.user.next(null);
    this.permission.next([]);
  }

  createFolder(request): void {
    return this.service.createFolder(request);
  }

  sendActivation(request: string): Observable<any> {
    return new Observable(observer => {
      this.service.sendActivation(request).subscribe({
        next: (res) => {
          this.toast.success('An email has been sent to ' + request + ' that will include a link to activate your account.');
          observer.next(res);
          observer.complete();
        },
        error: (err) => {
          console.error('Activation email failed', err);
          this.toast.fail('Failed to send activation email. Please try again later.');
          observer.error(err);
        }
      });
    });
  }

  resetPassword(request: string): Observable<any> {
    return new Observable(observer => {
      this.service.resetPassword(request).subscribe({
        next: (res) => {
          this.toast.success('An email has been sent to ' + request + ' that will include a link to reset the password.');
          observer.next(res);
          observer.complete();
        },
        error: (err) => {
          console.error('Password reset failed', err);
          this.toast.fail('Failed to send password reset email. Please try again later.');
          observer.error(err);
        }
      });
    });
  }

  enableDisableOfferUser(request: UserOfferUpdateApiRequest): any {
    if (offerBase !== undefined) {
      return this.service.enableDisableOfferUser(request);
    } else {
      return null;
    }
  }

}
